'use client'

import dynamic from 'next/dynamic'
import Image from 'next/image'
import { Grid, Typography, Box, IconButton } from '@mui/material'

// Types
import type { ServiceMenuCardProps } from '@/types/components/pages/business/service-menu-card'

// Utils
import { getServiceUnitPrice, getServiceOriginalPrice } from '@/utils/price/menu-service-price'
import ToPersianNumber from '@/utils/number/to-persian-number'

const NoImageIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/no-image'))
const AddIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/add'))

const ServiceMenuCard = (props: ServiceMenuCardProps) => {
	const { locale, service, colors, quantityInCart, isNew, onOpen, onAdd } = props

	// Variables
	const serviceImageSize = { xs: 120, md: 160 } as const
	const serviceRowMinHeight = { xs: 120, md: 160 } as const
	const hasDiscount = Boolean(service.prices?.length && service.prices[0].price_discount > 0)
	const unitPrice = getServiceUnitPrice(service)
	const originalPrice = getServiceOriginalPrice(service)
	const currency = service.prices?.[0]?.currency
	const isRialOrToman = currency?.code === 'IRR' || currency?.symbol === 'ریال' || currency?.symbol === 'تومان'
	const currencySymbol = isRialOrToman ? locale.common.title.toman : (currency?.symbol ?? '')

	const formatDisplayPrice = (price: number): { value: string; unit: string | null } => {
		if (isRialOrToman && price >= 1_000_000) {
			const millions = Math.round((price / 1_000_000) * 1000) / 1000
			return { value: ToPersianNumber(millions.toString()), unit: locale.common.title.million }
		}

		if (isRialOrToman && price >= 1_000) {
			const thousands = Math.round(price / 1_000)
			return { value: ToPersianNumber(thousands.toString()), unit: locale.common.title.thousand }
		}

		return { value: ToPersianNumber(new Intl.NumberFormat().format(price)), unit: null }
	}

	const unitPriceDisplay = formatDisplayPrice(unitPrice)
	const originalPriceDisplay = formatDisplayPrice(originalPrice)

	return (
		<Box
			sx={{
				height: 1,
				overflow: 'hidden',
				borderRadius: `${colors.radius}px`,
				bgcolor: colors.bgCard,
				border: `1px solid ${colors.border}`,
				boxShadow: colors.shadow,
				transition: 'box-shadow 0.5s cubic-bezier(0.22, 1, 0.36, 1), transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
				'@media (hover: hover)': {
					'&:hover': { boxShadow: colors.shadowHover, transform: 'translateY(-4px)' }
				}
			}}
		>
			<Grid container wrap="nowrap" sx={{ minHeight: serviceRowMinHeight }}>
				{/* Content */}
				<Grid size="grow" sx={{ minWidth: 0 }}>
					<Box
						onClick={onOpen}
						sx={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							p: { xs: 1.5, md: 2 },
							height: 1,
							cursor: 'pointer'
						}}
					>
						<Box>
							<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
								<Typography sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 700, color: colors.text, lineHeight: 1.35 }}>{service.name}</Typography>

								{isNew && (
									<Box
										component="span"
										sx={{
											fontSize: 11,
											fontWeight: 700,
											lineHeight: 1,
											px: 1,
											py: 0.5,
											borderRadius: 999,
											bgcolor: colors.newBadge,
											color: colors.newBadgeContrast
										}}
									>
										{locale.common.onlineMenu.new}
									</Box>
								)}
							</Box>

							{service?.fields_values?.length && service?.fields_values?.length > 0 ? (
								<Grid container spacing={0.5} sx={{ mt: 0.75 }}>
									{service?.fields_values?.map((field, index) => (
										<Grid key={index} size="auto">
											<Box sx={{ fontSize: 11, fontWeight: 600, px: 1, py: 0.25, borderRadius: 999, bgcolor: colors.bgMuted, color: colors.label }}>
												{field.value}
											</Box>
										</Grid>
									))}
								</Grid>
							) : null}

							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 1 }}>
								{hasDiscount && (
									<Typography sx={{ fontSize: 12, textDecoration: 'line-through', color: colors.label }}>
										{originalPriceDisplay.value}
										{originalPriceDisplay.unit && ` ${originalPriceDisplay.unit}`}
										{currencySymbol && ` ${currencySymbol}`}
									</Typography>
								)}

								<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.5 }}>
									<Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 700, color: colors.text }}>
										{unitPriceDisplay.value}
										{unitPriceDisplay.unit && (
											<Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: colors.bgHero, ml: 0.5 }}>
												{unitPriceDisplay.unit}
											</Box>
										)}
									</Typography>

									{currencySymbol && <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.bgHero }}>{currencySymbol}</Typography>}
								</Box>
							</Box>
						</Box>

						{service.description && (
							<Typography
								sx={{
									mt: 1,
									fontSize: 13,
									color: colors.textMuted,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									lineHeight: 1.5
								}}
							>
								{service.description}
							</Typography>
						)}
					</Box>
				</Grid>

				{/* Image */}
				<Grid
					size="auto"
					sx={{
						position: 'relative',
						width: serviceImageSize,
						minWidth: serviceImageSize,
						flexShrink: 0,
						bgcolor: colors.bgMuted,
						cursor: 'pointer'
					}}
					onClick={onOpen}
				>
					{service.image ? (
						<Image
							src={`/api/download${service.image}`}
							alt={service.name}
							fill
							sizes="(max-width: 899px) 120px, 160px"
							style={{ objectFit: 'cover', objectPosition: 'center' }}
						/>
					) : (
						<Box sx={{ display: 'grid', placeItems: 'center', width: serviceImageSize, height: serviceRowMinHeight, opacity: 0.35 }}>
							<NoImageIconAtom />
						</Box>
					)}

					<Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
						<IconButton
							size="small"
							onClick={e => {
								e.stopPropagation()
								onAdd()
							}}
							aria-label={locale.common.onlineMenu.addToOrder}
							sx={{
								width: 36,
								height: 36,
								bgcolor: '#f5f5f5',
								color: colors.text,
								fontSize: quantityInCart > 0 ? 14 : undefined,
								fontWeight: 700,
								borderRadius: 1.2,
								border: `1.2px solid ${colors.border}`,
								transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.2s ease',
								'&:hover': { bgcolor: '#fff' },
								'&:active': { transform: 'scale(0.88)' }
							}}
						>
							{quantityInCart > 0 ? ToPersianNumber(quantityInCart.toString()) : <AddIconAtom sx={{ fontSize: 20 }} />}
						</IconButton>
					</Box>
				</Grid>
			</Grid>
		</Box>
	)
}

export default ServiceMenuCard
